import { useEffect, useState } from "react";
import { FileText, Sparkles, RefreshCw, ExternalLink, Loader2, Plus } from "lucide-react";
import { api, formatApiError } from "../api/client";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import OrgStructureForm from "./OrgStructureForm";
import { toast } from "sonner";

const STATE_BADGE = {
  missing: { label: "Pendiente", cls: "bg-slate-100 text-slate-500" },
  partial: { label: "Borrador en curso", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  ok: { label: "Completado", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
};

function StateBadge({ state }) {
  const s = STATE_BADGE[state] || STATE_BADGE.missing;
  return <Badge className={s.cls} data-testid="deliverable-state">{s.label}</Badge>;
}

export default function DeliverableBlock({ step, status, canCreateDoc, navigate, onGenerate, generating, onChanged }) {
  const deliverable = step.deliverable || {};
  const mode = deliverable.mode;

  return (
    <div className="mt-6 bg-white border border-slate-200 rounded-md p-5" data-testid="deliverable-block">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className="text-[14px] font-semibold text-slate-900">Entregable de este paso</span>
          {deliverable.mandatory_for_cert && <Badge variant="outline" className="text-[11px] text-rose-600 border-rose-200">Obligatorio para certificar</Badge>}
        </div>
        {status && <StateBadge state={status.state} />}
      </div>
      <p className="text-[13px] text-slate-600 mb-3">{deliverable.deliverable_name}</p>

      {mode === "single_document" && (
        <SingleDocument status={status} canCreateDoc={canCreateDoc} navigate={navigate} onGenerate={onGenerate} generating={generating} />
      )}
      {mode === "existing_artifact" && (
        <Button variant="outline" onClick={() => navigate("/soa?framework=iso27001")} data-testid="deliverable-open-soa">
          <ExternalLink className="h-4 w-4 mr-1" /> Abrir la Declaración de Aplicabilidad (SoA)
        </Button>
      )}
      {mode === "multi_control" && (
        <MultiControl step={step} status={status} canCreateDoc={canCreateDoc} navigate={navigate} />
      )}
      {mode === "structured_form" && (
        <div>
          <OrgStructureForm canWrite={canCreateDoc} onChanged={onChanged} />
          {canCreateDoc && (
            <div className="mt-4 pt-3 border-t border-slate-100">
              <Button onClick={onGenerate} disabled={generating} data-testid="deliverable-generate-raci">
                {generating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
                Generar documento de responsabilidades (RACI)
              </Button>
              <p className="text-[12px] text-slate-500 mt-2">Crea un borrador con tu matriz. Lo revisas y lo apruebas tú; no se aprueba solo.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SingleDocument({ status, canCreateDoc, navigate, onGenerate, generating }) {
  const docs = status?.documents || [];
  const approved = docs.find((d) => d.status === "approved");
  const draftId = status?.draft_document_id;

  if (!canCreateDoc) return <p className="text-[13px] text-slate-400">No tienes permiso para generar documentos.</p>;

  if (approved) {
    return (
      <div className="flex gap-2">
        <Button variant="outline" onClick={() => navigate(`/documents/${approved.id}`)} data-testid="deliverable-open-doc">
          <FileText className="h-4 w-4 mr-1" /> Abrir documento aprobado
        </Button>
        <Button variant="ghost" onClick={onGenerate} disabled={generating} data-testid="deliverable-regenerate">
          {generating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />} Generar nueva versión
        </Button>
      </div>
    );
  }
  if (draftId) {
    return (
      <div className="flex gap-2">
        <Button onClick={() => navigate(`/documents/${draftId}`)} data-testid="deliverable-open-draft">
          <FileText className="h-4 w-4 mr-1" /> Abrir borrador
        </Button>
        <Button variant="outline" onClick={onGenerate} disabled={generating} data-testid="deliverable-regenerate">
          {generating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />} Regenerar
        </Button>
      </div>
    );
  }
  return (
    <div>
      <Button onClick={onGenerate} disabled={generating} data-testid="deliverable-generate">
        {generating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />} Generar borrador del entregable
      </Button>
      <p className="text-[12px] text-slate-500 mt-2">La IA redacta un borrador a partir de tus datos. Llega en estado borrador con una guía de revisión; lo apruebas tú.</p>
    </div>
  );
}

function MultiControl({ step, status, canCreateDoc, navigate }) {
  const [controls, setControls] = useState([]);
  const [docMap, setDocMap] = useState({});
  const [loading, setLoading] = useState(true);
  const prefix = step.related_control_prefix;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [soa, docs] = await Promise.all([
          api.get("/soa?framework=iso27001"),
          api.get("/documents?page_size=200"),
        ]);
        if (cancelled) return;
        const items = (soa.data.items || []).filter(
          (it) => it.control.code.startsWith(`${prefix}.`) && (it.implementation?.applicability ?? "applicable") === "applicable"
        );
        setControls(items);
        const map = {};
        (docs.data.items || []).forEach((d) => {
          (d.related_controls || []).forEach((c) => {
            if (!map[c] || d.status === "approved") map[c] = d.status;
          });
        });
        setDocMap(map);
      } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [prefix]);

  const genFor = async (code) => {
    try {
      const { data } = await api.post("/documents", {
        title: `${code} — Implementación`, type: "record",
        related_controls: [code], from_step_code: step.code, journey_step_code: step.code,
      });
      toast.success("Borrador creado y vinculado al control. Redáctalo en el editor.");
      navigate(`/documents/${data.id}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (loading) return <div className="text-[13px] text-slate-400"><Loader2 className="h-4 w-4 inline animate-spin mr-1" /> Cargando controles...</div>;

  return (
    <div>
      <div className="text-[13px] text-slate-600 mb-2">
        {status?.approved ?? 0} de {status?.applicable ?? controls.length} controles aplicables con documento aprobado.
      </div>
      <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-80 overflow-y-auto">
        {controls.map((it) => {
          const st = docMap[it.control.code];
          const label = st === "approved" ? "Aprobado" : (st ? "Borrador" : "Sin documento");
          const cls = st === "approved" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : (st ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-slate-100 text-slate-500");
          return (
            <div key={it.control.code} className="flex items-center gap-2 px-3 py-2" data-testid={`mc-control-${it.control.code}`}>
              <span className="text-[12px] font-mono text-slate-500 w-16">{it.control.code}</span>
              <span className="flex-1 text-[13px] text-slate-800 truncate">{it.control.title}</span>
              <Badge className={`${cls} text-[11px]`}>{label}</Badge>
              <Button variant="ghost" size="sm" onClick={() => navigate(`/controls/${it.implementation?.id}`)} data-testid={`mc-open-${it.control.code}`} disabled={!it.implementation?.id}>Abrir</Button>
              {canCreateDoc && !st && <Button variant="outline" size="sm" onClick={() => genFor(it.control.code)} data-testid={`mc-gen-${it.control.code}`}><Plus className="h-3.5 w-3.5" /></Button>}
            </div>
          );
        })}
        {controls.length === 0 && <div className="px-3 py-5 text-center text-[13px] text-slate-400">No hay controles aplicables en este grupo. Revisa el SoA.</div>}
      </div>
    </div>
  );
}
